"use client";

import React from "react";
import { Button } from "../common/button";

export interface JourneyStep {
  title: string;
  description: string;
}

export interface YourJourneyProps {
  title?: string;
  subtitle?: string;
  steps?: JourneyStep[];
  ctaText?: string;
  onCtaClick?: () => void;
  showCta?: boolean;
}

export function YourJourney({
  title = "Your Journey at Alli",
  subtitle = "Choose a therapist who understands your unique story, decide on a rate that fits your lifestyle, and begin a journey towards personal growth.",
  steps = [
    {
      title: "Pick Your Therapy Price",
      description: "With flexible rates from $45 for 50 minutes, pick a session rate that aligns with your life's rhythm."
    },
    {
      title: "Uncover Your Therapist Matches",
      description: "Take our 2-minute quiz and get matched with therapists who resonate with your unique needs."
    },
    {
      title: "Embark On Your Journey",
      description: "Choose a therapist who feels right for you. With their support, you'll gain tools to help you navigate life with."
    }
  ],
  ctaText = "Get Matched",
  onCtaClick,
  showCta = true
}: YourJourneyProps) {
  const bubbleColors = ["#dde3ed", "#fbe4d7", "#dde3ed"];
  return (
    <section className="py-16 md:py-24 px-4 bg-white">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12 md:mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 md:mb-6">
            {title}
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed px-4 md:px-0">
            {subtitle}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-8 mb-12 md:mb-16 max-w-6xl mx-auto relative">
          {steps.map((step, index) => (
            <div key={index} className="relative text-center">
              <div className="flex flex-col items-center">
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center mb-4 md:mb-6"
                  style={{ backgroundColor: bubbleColors[index] }}
                >
                  <span className="text-2xl font-bold text-gray-700">{index + 1}</span>
                </div>
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3 md:mb-4">
                  {step.title}
                </h3>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed max-w-xs mx-auto">
                  {step.description}
                </p>
              </div>
              
              {/* Dotted wave connector between steps - using Alli's actual SVG */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-5 left-[70%] w-[220px]">
                  <img 
                    src="https://cdn.prod.website-files.com/6110b06f10260e518f5d3077/6110b1396056a5e6b0bd01f9_Dot%20Wave.svg" 
                    alt="" 
                    className="w-full h-auto opacity-90"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        
        {showCta && (
          <div className="text-center">
            <Button 
              size="lg" 
              className="bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 text-lg"
              onClick={onCtaClick}
            >
              {ctaText}
            </Button>
          </div>
        )}
      </div>
    </section>
  );
}